import {
  ApiFehler,
  VerkaufGestoppt,
  type BelegAntwort,
  type JournalBeleg,
  type KassenApi,
  type KasseStatus,
} from '../api';

/**
 * Retoure Stufe 2 – **einzige Stelle des API-Vertrags** in der Kasse.
 *
 * Vertrag: `docs/retoure/api-vertrag-stufe2.md` (Branch `feature/retoure-api`, Fassung 04.11.2026).
 * Alle Pfade, Anfrageformen und die Auswertung der Antworten stehen hier; die Oberfläche
 * (`Retoure.tsx`) kennt nur {@link RetoureApi} mit Mengen als Zahl (kg mit Dezimalen) und
 * Beträgen in Cent. Tests ersetzen die Schnittstelle durch ein Mock.
 *
 * Grundsätze:
 * - Die Kasse sendet nie Beträge (außer der Bestätigung des vom Server genannten
 *   Auszahlungsbetrags), nur Index, Menge (`mengeMilli`), Zustand, Grund und Erstattungsart.
 * - Ein unbekannter und ein fremder Beleg sind beide „nicht gefunden“ (Vertrag 1, 4.1).
 * - Die Vorgangs-ID gilt für Vorschau, Freigabe, Abschluss und Auszahlung; ist der Ausgang des
 *   Abschlusses unklar, klärt nur `GET …/retouren/:vorgangId` (Vertrag 4.6).
 */

export type Zahlart = 'bar' | 'karte' | 'gutschein' | 'gemischt';

/** Gründe laut Vertrag 3.3 (Fassung 04.11.2026 (2)): ein Grund je Vorgang. */
export type GrundCode = 'defekt' | 'fehlkauf' | 'umtausch' | 'sonstiges';
export type Zustand = 'verkaufsfaehig' | 'beschaedigt';

export interface RetoureFaehigkeit {
  /** Der Server meldet die Retoure für diese Kasse als nutzbar. */
  verfuegbar: boolean;
  /** Grund, wenn nicht verfügbar (Route aus, nicht freigeschaltet, Schema fehlt, kein Recht). */
  grund: string | null;
}

export interface RetourePosition {
  index: number;
  bezeichnung: string;
  steuersatz: string;
  /** `kg`: Menge mit bis zu 3 Nachkommastellen; sonst Stück. */
  einheit: 'stk' | 'kg';
  gekauft: number;
  bisherZurueck: number;
  moeglich: number;
  /** Nicht retournierbar (Pfand, Gutscheinverkauf, Rabattzeile …) – Text vom Server. */
  sperrgrund: string | null;
  /** Nur als ganze Restmenge rückgebbar. */
  nurGanz: boolean;
}

export interface RetoureOriginal {
  kassenId: string;
  belegnummer: string;
  zeitpunkt: string;
  zahlungsart: 'bar' | 'karte' | 'gutschein' | null;
  summeCent: number;
  /** Original rückgabefähig (`erstattbar`). */
  rueckgabefaehig: boolean;
  /** Sperrgründe (Text vom Server) – leer, wenn rückgabefähig. */
  gruende: string[];
  /** Original bereits vollständig zurückgegeben (`vollstaendig_zurueck`). */
  vollstaendigZurueck: boolean;
  /** Erstattungsart laut Server (`bar`) bzw. null = keine Erstattung möglich. */
  erstattungsart: 'bar' | null;
  /** Sperrtext zur Erstattung (`erstattung_gesperrt`), z. B. „Kartenerstattung nicht freigegeben“. */
  erstattungSperre: string | null;
  freigabeAbCent: number | null;
  positionen: RetourePosition[];
  /** Bisherige Retouren zu diesem Original (alle Kassen). */
  retouren: { belegnummer: string; kassenId: string; zeitpunkt: string; summeCent: number }[];
}

/** Auswahl eines Vorgangs (Vertrag 3.1) – Mengen als Zahl, Umrechnung in `mengeMilli` hier. */
export interface RetoureAnfrage {
  original: { kassenId: string; belegnummer: string };
  grund: GrundCode;
  grundText: string | null;
  positionen: { index: number; menge: number; zustand: Zustand }[];
  erstattungsart: 'bar';
}

export interface RetoureVorschau {
  summeCent: number;
  /** Anteiliger Bon-Rabatt (≤ 0), Summe der Zeilen, nur Anzeige. */
  rabattCent: number;
  jeSteuersatz: { steuersatz: string; bruttoCent: number; nettoCent: number; ustCent: number }[];
  zeilen: { index: number; bezeichnung: string; menge: number; erstattungCent: number }[];
  freigabeAbCent: number | null;
  freigabeNoetig: boolean;
}

export interface Freigabe {
  bedienerId: number;
  pin: string;
}

export interface FreigabeErgebnis {
  name: string;
  gueltigBis: string | null;
}

export interface RetoureAbschluss {
  vorgangId: string;
  beleg: BelegAntwort;
  /** Belegtext laut Server (`druck.text`), null = über `…/druck` nachladen. */
  text: string | null;
  auszahlungCent: number;
  auszahlungBestaetigt: boolean;
  original: { kassenId: string; belegnummer: string };
  /** Lager je Position (`gebucht` bzw. `klaerung`). */
  lager: { index: number; ergebnis: string }[];
}

/** Ausgang des Abschlusses bzw. der Abfrage nach der Vorgangs-ID. */
export type RetoureErgebnis =
  | ({ art: 'abgeschlossen' } & RetoureAbschluss)
  /** Server hat den Vorgang, aber der Stand ist unklar (z. B. Antwort ohne Beleg). */
  | { art: 'in_pruefung'; vorgangId: string; text: string }
  /** Endgültig abgelehnt: kein Beleg, nichts auszahlen. */
  | { art: 'abgelehnt'; vorgangId: string; text: string; code: string | null }
  /** Nur bei `stand`: An dieser Kasse gibt es keinen Beleg zu dieser ID (404 `nicht_vorhanden`). */
  | { art: 'unbekannt'; vorgangId: string };

export class BelegNichtGefunden extends Error {
  constructor() {
    // mandantensicher: derselbe Text für unbekannte und fremde Belege (RT2-7)
    super('Beleg nicht gefunden. Belegnummer prüfen oder Bon erneut scannen.');
  }
}

/** Belegnummer an mehreren Kassen des Unternehmers (409 `original_mehrdeutig`). */
export class OriginalMehrdeutig extends Error {
  constructor(readonly kassen: string[]) {
    super('Diese Belegnummer gibt es an mehreren Kassen – bitte die Kasse wählen.');
  }
}

/** Ablehnung durch den Server mit Fehlercode (Vertrag 6), Meldung zur Anzeige. */
export class RetoureAbgelehnt extends Error {
  constructor(
    readonly code: string | null,
    meldung: string,
    readonly status: number,
  ) {
    super(meldung);
  }
}

/** Ausgang unklar (Netz, Zeitlimit, 503 `klaerungsbeduerftig`, Konflikt): nur über `stand`. */
export class RetoureUnklar extends Error {}

export interface RetoureApi {
  faehigkeit(kassenId: string): Promise<RetoureFaehigkeit>;
  status(kassenId: string): Promise<KasseStatus>;
  /** `ziel.qr`: vollständiger Datenblock; sonst Belegnummer (optional mit Kasse). */
  original(
    kassenId: string,
    ziel: { qr: string } | { belegnummer: string; kassenId: string | null },
  ): Promise<RetoureOriginal>;
  vorschau(kassenId: string, vorgangId: string, anfrage: RetoureAnfrage): Promise<RetoureVorschau>;
  freigabe(
    kassenId: string,
    vorgangId: string,
    anfrage: RetoureAnfrage,
    freigabe: Freigabe,
  ): Promise<FreigabeErgebnis>;
  /** Wirft {@link RetoureUnklar}, wenn der Ausgang offen ist; sonst ein Ergebnis. */
  abschliessen(
    kassenId: string,
    vorgangId: string,
    anfrage: RetoureAnfrage,
    geraetekennung: string,
  ): Promise<RetoureErgebnis>;
  stand(kassenId: string, vorgangId: string): Promise<RetoureErgebnis>;
  /** Auszahlung durch die Kassierkraft bestätigt (Ereignis, kein Beleg). */
  auszahlungBestaetigen(kassenId: string, vorgangId: string, betragCent: number): Promise<void>;
  /** Belegtext des signierten Retourenbelegs (bestehender Abruf `…/belege/:nr/druck`). */
  belegText(kassenId: string, belegnummer: string): Promise<string>;
}

/* ── Umrechnung und Auswertung (defensiv: Unbekanntes sperrt, nie raten) ─────────── */

type Roh = Record<string, unknown>;

function obj(x: unknown): Roh {
  return typeof x === 'object' && x !== null && !Array.isArray(x) ? (x as Roh) : {};
}
function text(x: unknown): string | null {
  return typeof x === 'string' && x !== '' ? x : null;
}
function cent(x: unknown): number {
  return typeof x === 'number' && Number.isSafeInteger(x) ? x : Number.NaN;
}
function liste(x: unknown): unknown[] {
  return Array.isArray(x) ? x : [];
}
/** `mengeMilli` (Ganzzahl) → Menge; ungültig = NaN. */
function ausMilli(x: unknown): number {
  return typeof x === 'number' && Number.isSafeInteger(x) ? x / 1000 : Number.NaN;
}
/** Menge → `mengeMilli` (Ganzzahl in Tausendsteln, nie gerundet über 3 Stellen hinaus). */
export function inMilli(menge: number): number {
  return Math.round(menge * 1000);
}

/** Fehlercode und Meldung einer Ablehnung (Vertrag 1 „Fehler“). */
export function fehlerCode(e: unknown): string | null {
  return e instanceof ApiFehler ? text(e.daten?.code) : null;
}

export function leseOriginal(roh: unknown): RetoureOriginal {
  const o = obj(roh);
  const orig = obj(o.original);
  const sperren = liste(o.sperrgruende).map((s) => obj(s));
  const positionen = liste(o.positionen).map((p): RetourePosition => {
    const r = obj(p);
    const gekauft = ausMilli(r.mengeMilli);
    const bisher = ausMilli(r.bisherZurueckMilli);
    const rest = ausMilli(r.restMilli);
    const ungueltig = [gekauft, bisher, rest].some((n) => Number.isNaN(n));
    const faehig = r.rueckgabefaehig === true && !ungueltig;
    return {
      index: typeof r.index === 'number' ? r.index : -1,
      bezeichnung: text(r.bezeichnung) ?? '–',
      steuersatz: text(r.steuersatz) ?? '?',
      einheit: r.einheit === 'kg' ? 'kg' : 'stk',
      gekauft: ungueltig ? 0 : gekauft,
      bisherZurueck: ungueltig ? 0 : bisher,
      moeglich: ungueltig || !faehig ? 0 : rest,
      sperrgrund: ungueltig
        ? 'Antwort des Servers unvollständig'
        : faehig
          ? null
          : (text(r.sperrgrund) ?? 'Nicht rückgabefähig'),
      nurGanz: r.nurGanz === true,
    };
  });
  const zahlungsart = orig.zahlungsart;
  const vollstaendig = sperren.some((s) => s.code === 'vollstaendig_zurueck');
  const erstattungsart = o.erstattungsart === 'bar' ? 'bar' : null;
  return {
    kassenId: text(orig.kassenId) ?? '',
    belegnummer: text(orig.belegnummer) ?? '',
    zeitpunkt: text(orig.zeitpunkt) ?? '',
    zahlungsart:
      zahlungsart === 'bar' || zahlungsart === 'karte' || zahlungsart === 'gutschein'
        ? zahlungsart
        : null,
    summeCent: cent(orig.summeCent),
    rueckgabefaehig: o.erstattbar === true && erstattungsart !== null,
    gruende: sperren
      .filter((s) => s.code !== 'vollstaendig_zurueck')
      .map((s) => text(s.fehler) ?? 'Rückgabe gesperrt'),
    vollstaendigZurueck: vollstaendig,
    erstattungsart,
    erstattungSperre:
      sperren.filter((s) => s.code === 'erstattung_gesperrt').map((s) => text(s.fehler))[0] ?? null,
    freigabeAbCent: Number.isNaN(cent(o.freigabeAbCent)) ? null : cent(o.freigabeAbCent),
    positionen,
    retouren: liste(o.retouren).map((x) => {
      const r = obj(x);
      return {
        belegnummer: text(r.belegnummer) ?? '–',
        kassenId: text(r.kassenId) ?? '–',
        zeitpunkt: text(r.zeitpunkt) ?? '',
        summeCent: cent(r.summeCent),
      };
    }),
  };
}

export function leseVorschau(roh: unknown): RetoureVorschau {
  const o = obj(roh);
  const zeilen = liste(o.zeilen).map((z) => obj(z));
  const summeCent = cent(o.summeCent);
  if (Number.isNaN(summeCent)) {
    throw new RetoureAbgelehnt(null, 'Vorschau des Servers ohne gültigen Betrag', 0);
  }
  const ab = cent(o.freigabeAbCent);
  return {
    summeCent,
    rabattCent: zeilen.reduce((s, z) => s + (cent(z.rabattAnteilCent) || 0), 0),
    jeSteuersatz: liste(o.jeSteuersatz).map((s) => {
      const r = obj(s);
      return {
        steuersatz: text(r.steuersatz) ?? '?',
        bruttoCent: cent(r.bruttoCent),
        nettoCent: cent(r.nettoCent),
        ustCent: cent(r.ustCent),
      };
    }),
    zeilen: zeilen.map((r) => ({
      index: typeof r.index === 'number' ? r.index : -1,
      bezeichnung: text(r.bezeichnung) ?? '–',
      menge: ausMilli(r.mengeMilli),
      erstattungCent: cent(r.erstattungCent),
    })),
    freigabeAbCent: Number.isNaN(ab) ? null : ab,
    // fehlt die Angabe, gilt die Freigabe als nötig (sicherer Fall)
    freigabeNoetig: o.freigabeNoetig !== false,
  };
}

function leseBeleg(x: unknown): BelegAntwort | null {
  const b = obj(x);
  return typeof b.belegnummer === 'string' && typeof b.kassenId === 'string'
    ? (b as unknown as BelegAntwort)
    : null;
}

/** Antwort von Abschluss bzw. Abfrage (Vertrag 4.4/4.6); unvollständig = „in Prüfung“. */
export function leseErgebnis(roh: unknown, vorgangId: string): RetoureErgebnis {
  const o = obj(roh);
  const id = text(o.vorgangId) ?? vorgangId;
  if (id !== vorgangId) {
    return { art: 'in_pruefung', vorgangId, text: 'Antwort gehört zu einem anderen Vorgang.' };
  }
  // Vertrag 4.6 (Stand 7669d615): festgeschrieben → auszahlung_offen | ausbezahlt; die früheren
  // Werte abgeschlossen | ausgezahlt bleiben lesbar
  const ausbezahlt = o.status === 'ausbezahlt' || o.status === 'ausgezahlt';
  if (
    ausbezahlt ||
    o.status === 'auszahlung_offen' ||
    o.status === 'abgeschlossen' ||
    o.festgeschrieben === true
  ) {
    const beleg = leseBeleg(o.beleg);
    const auszahlung = obj(o.auszahlung);
    const betrag = cent(auszahlung.betragCent);
    const orig = obj(o.original);
    if (beleg !== null && !Number.isNaN(betrag)) {
      return {
        art: 'abgeschlossen',
        vorgangId: id,
        beleg,
        text: text(obj(o.druck).text),
        auszahlungCent: betrag,
        auszahlungBestaetigt: auszahlung.bestaetigt === true || ausbezahlt,
        original: {
          kassenId: text(orig.kassenId) ?? '',
          belegnummer: text(orig.belegnummer) ?? '',
        },
        lager: liste(o.lager).map((l) => {
          const r = obj(l);
          return {
            index: typeof r.index === 'number' ? r.index : -1,
            ergebnis: text(r.ergebnis) ?? '?',
          };
        }),
      };
    }
  }
  return { art: 'in_pruefung', vorgangId: id, text: 'Antwort des Servers unvollständig.' };
}

/**
 * Ist der Ausgang des Abschlusses unklar? Netz, Zeitlimit, jeder 5xx außer `retoure_schema_fehlt`
 * und 409 `vorgang_konflikt` (die ID gibt es schon – nur die Abfrage zeigt, mit welchem Ergebnis).
 */
export function istUnklar(e: unknown): boolean {
  if (e instanceof ApiFehler) {
    const code = fehlerCode(e);
    if (e.status === 0) {
      return true;
    }
    if (e.status >= 500) {
      return code !== 'retoure_schema_fehlt';
    }
    return e.status === 409 && code === 'vorgang_konflikt';
  }
  // Netzabbruch (TypeError) und jede unbekannte Ausnahme: unklar
  return true;
}

/* ── Kennzeichen für die Belegliste ────────────────────────────────────────────────── */

/** Retourenbelege tragen die Nummer `R-JJJJMMTT-GGGG-NNNN` (Vertrag 4.4, 7). */
export function istRetourenbeleg(belegnummer: string): boolean {
  return /^R-\d{8}-[A-Z0-9]{4}-\d+$/.test(belegnummer);
}

/** Kennzeichen eines Journaleintrags: „RÜCKGABE“ für Retourenbelege, sonst null. */
export function retoureKennzeichen(
  b: Pick<JournalBeleg, 'belegnummer' | 'belegtyp'>,
): string | null {
  return b.belegtyp === 'STANDARD' && istRetourenbeleg(b.belegnummer) ? 'RÜCKGABE' : null;
}

/** Rückgabestand eines Originals aus der Suche (für die Belegansicht). */
export function rueckgabeStand(o: RetoureOriginal): string | null {
  if (o.vollstaendigZurueck) {
    return 'vollständig zurückgegeben';
  }
  return o.positionen.some((p) => p.bisherZurueck > 0) || o.retouren.length > 0
    ? 'teilweise zurückgegeben'
    : null;
}

/* ── Umsetzung über die KassenApi ──────────────────────────────────────────────────── */

const PFAD = '/retouren';

function koerper(vorgangId: string, a: RetoureAnfrage): Record<string, unknown> {
  return {
    vorgangId,
    original: a.original,
    positionen: [...a.positionen]
      .sort((x, y) => x.index - y.index)
      .map((p) => ({ index: p.index, mengeMilli: inMilli(p.menge), zustand: p.zustand })),
    grund: a.grund,
    grundText: a.grundText,
    erstattungsart: a.erstattungsart,
  };
}

/** Ablehnung in eine Anzeige übersetzen; Netz/Zeitlimit bleiben als ApiFehler(0) erkennbar. */
function alsAblehnung(e: unknown): unknown {
  if (e instanceof ApiFehler && e.status >= 400 && e.status < 500) {
    return new RetoureAbgelehnt(fehlerCode(e), e.message, e.status);
  }
  return e;
}

export function retoureApiAus(api: KassenApi): RetoureApi {
  return {
    /**
     * Kein eigener Abruf im Vertrag: Das Journal (Recht `retoure`) zeigt, ob die Routen
     * registriert und für den Unternehmer freigeschaltet sind (404 = Schalter aus).
     */
    faehigkeit: async (kassenId) => {
      try {
        await api.retoureAbruf('GET', kassenId, PFAD);
        return { verfuegbar: true, grund: null };
      } catch (e) {
        const code = fehlerCode(e);
        if (e instanceof ApiFehler && e.status === 404) {
          return { verfuegbar: false, grund: 'Retoure ist am Server nicht eingeschaltet.' };
        }
        if (code === 'retoure_nicht_freigeschaltet' || code === 'retoure_schema_fehlt') {
          return {
            verfuegbar: false,
            grund: 'Retoure ist für dieses Unternehmen nicht freigeschaltet.',
          };
        }
        if (code === 'recht_fehlt') {
          return {
            verfuegbar: false,
            grund: 'Kein Recht „Retoure“ für den angemeldeten Bediener.',
          };
        }
        if (code === 'bediener_sitzung_fehlt') {
          return { verfuegbar: false, grund: 'Für die Retoure zuerst als Bediener anmelden.' };
        }
        throw e;
      }
    },
    status: (kassenId) => api.status(kassenId),
    original: async (kassenId, ziel) => {
      const p = new URLSearchParams();
      if ('qr' in ziel) {
        p.set('qr', ziel.qr);
      } else {
        p.set('belegnummer', ziel.belegnummer);
        if (ziel.kassenId !== null) {
          p.set('originalKasse', ziel.kassenId);
        }
      }
      try {
        return leseOriginal(
          await api.retoureAbruf('GET', kassenId, `${PFAD}/original?${p.toString()}`),
        );
      } catch (e) {
        if (e instanceof ApiFehler && e.status === 404) {
          throw new BelegNichtGefunden();
        }
        if (e instanceof ApiFehler && fehlerCode(e) === 'original_mehrdeutig') {
          const kassen = liste(e.daten?.kassen).filter((k): k is string => typeof k === 'string');
          throw new OriginalMehrdeutig(kassen);
        }
        throw alsAblehnung(e);
      }
    },
    vorschau: async (kassenId, vorgangId, anfrage) => {
      try {
        return leseVorschau(
          await api.retoureAbruf('POST', kassenId, `${PFAD}/vorschau`, koerper(vorgangId, anfrage)),
        );
      } catch (e) {
        throw alsAblehnung(e);
      }
    },
    freigabe: async (kassenId, vorgangId, anfrage, freigabe) => {
      try {
        const r = obj(
          await api.retoureAbruf('POST', kassenId, `${PFAD}/freigabe`, {
            ...koerper(vorgangId, anfrage),
            freigabe,
          }),
        );
        return {
          name: text(obj(r.freigabeDurch).name) ?? 'berechtigte Person',
          gueltigBis: text(r.gueltigBis),
        };
      } catch (e) {
        throw alsAblehnung(e);
      }
    },
    abschliessen: async (kassenId, vorgangId, anfrage, geraetekennung) => {
      try {
        const roh = await api.retoureAbschluss(kassenId, PFAD, {
          ...koerper(vorgangId, anfrage),
          geraetekennung,
        });
        return leseErgebnis(roh, vorgangId);
      } catch (e) {
        // Verkaufsstopp: nichts gesendet – endgültig, keine Klärung nötig
        if (e instanceof VerkaufGestoppt) {
          return { art: 'abgelehnt', vorgangId, text: e.message, code: 'verkaufsstopp' };
        }
        if (istUnklar(e)) {
          throw new RetoureUnklar(e instanceof Error ? e.message : String(e));
        }
        // 4xx (außer `vorgang_konflikt`) bzw. `retoure_schema_fehlt`: kein Beleg entstanden
        return {
          art: 'abgelehnt',
          vorgangId,
          text: e instanceof Error ? e.message : String(e),
          code: fehlerCode(e),
        };
      }
    },
    stand: async (kassenId, vorgangId) => {
      try {
        return leseErgebnis(
          await api.retoureAbruf('GET', kassenId, `${PFAD}/${encodeURIComponent(vorgangId)}`),
          vorgangId,
        );
      } catch (e) {
        // nur 404 `nicht_vorhanden` heißt „nicht angekommen“; ein 404 der ausgeschalteten Routen
        // (Server-Schalter aus) lässt den Vorgang in der Klärung (Codex R1 M5)
        if (e instanceof ApiFehler && e.status === 404 && fehlerCode(e) === 'nicht_vorhanden') {
          return { art: 'unbekannt', vorgangId };
        }
        throw e;
      }
    },
    belegText: (kassenId, belegnummer) => api.druck(kassenId, belegnummer),
    auszahlungBestaetigen: async (kassenId, vorgangId, betragCent) => {
      await api.retoureAbruf(
        'POST',
        kassenId,
        `${PFAD}/${encodeURIComponent(vorgangId)}/auszahlung`,
        { betragCent },
      );
    },
  };
}
